import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  Input,
  Select,
  Option,
  FormControl,
  FormLabel,
} from '@mui/joy';
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import BatchPostCard from './BatchPostCard';
import BatchPostComposer from './BatchPostComposer';
import RepliesSection from './RepliesSection';
import type {
  CongregationBatchPostWithDetails,
  BatchPostReactionType,
  CongregationMember,
  CongregationBatchPostInput,
} from '../../types/congregation';
import {
  listBatchPosts,
  addPostReaction,
  removePostReaction,
  deletePostReply,
  createBatchPost,
  deleteBatchPost,
  updateBatchPost,
} from '../../api';

interface BatchPostFeedProps {
  batchId: number;
  currentMember: CongregationMember | null;
  canPost?: boolean;
  canPin?: boolean;
  readOnly?: boolean;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

export default function BatchPostFeed({
  batchId,
  currentMember,
  canPost = false,
  canPin = false,
  readOnly = true,
  onError,
  onSuccess,
}: BatchPostFeedProps) {
  const [posts, setPosts] = React.useState<CongregationBatchPostWithDetails[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [expandedPost, setExpandedPost] = React.useState<number | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterType, setFilterType] = React.useState<'all' | 'pinned' | 'attachments' | 'polls'>('all');
  const [visibleCount, setVisibleCount] = React.useState(6);
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const loadedRef = React.useRef(false);

  // Load posts on mount
  React.useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    loadPosts();
    
    // Set up polling for real-time updates every 30 seconds
    const interval = setInterval(() => {
      if (!loadedRef.current) return;
      loadedRef.current = false;
      loadPosts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [batchId, currentMember?.id, onError]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await listBatchPosts(batchId, currentMember?.id);
      setPosts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load batch posts:', error);
      onError?.('Failed to load batch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPost = async (postData: CongregationBatchPostInput | string, isPinned?: boolean) => {
    try {
      setSubmitting(true);
      // If postData is a string, it's an edit (shouldn't happen in feed, but handle for type safety)
      if (typeof postData === 'string') {
        // This is edit mode - shouldn't be called from feed
        return;
      }
      // Otherwise it's compose mode
      await createBatchPost(batchId, postData);
      onSuccess?.('Post created successfully');
      await loadPosts();
    } catch (error) {
      console.error('Failed to create post:', error);
      onError?.('Failed to create post');
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleReaction = async (postId: number, reactionType: BatchPostReactionType) => {
    if (!currentMember) return;

    const post = posts.find((p) => p.id === postId);
    if (!post) return;

    // Optimistic update
    const oldReaction = post.myReaction;
    const oldCounts = post.reactionCounts
      ? (typeof post.reactionCounts === 'string' ? JSON.parse(post.reactionCounts) : post.reactionCounts)
      : {};

    try {
      // Update local state optimistically
      setPosts((prevPosts) =>
        prevPosts.map((p) => {
          if (p.id !== postId) return p;

          const newCounts = { ...oldCounts };
          if (oldReaction) {
            newCounts[oldReaction] = Math.max(0, (newCounts[oldReaction] || 0) - 1);
          }
          newCounts[reactionType] = (newCounts[reactionType] || 0) + 1;

          return {
            ...p,
            myReaction: oldReaction === reactionType ? null : reactionType,
            reactionCounts: JSON.stringify(newCounts) as any,
          };
        })
      );

      // Call API
      if (oldReaction === reactionType) {
        await removePostReaction(postId);
      } else {
        await addPostReaction(postId, reactionType);
      }
    } catch (error) {
      console.error('Failed to update reaction:', error);
      // Revert optimistic update
      setPosts((prevPosts) =>
        prevPosts.map((p) => {
          if (p.id !== postId) return p;
          return {
            ...p,
            myReaction: oldReaction,
            reactionCounts: JSON.stringify(oldCounts) as any,
          };
        })
      );
      onError?.('Failed to update reaction');
    }
  };

  const handleDeleteReply = async (replyId: number) => {
    try {
      await deletePostReply(replyId);
      onSuccess?.('Reply deleted');
    } catch (error) {
      console.error('Failed to delete reply:', error);
      onError?.('Failed to delete reply');
    }
  };

  const handleDeletePost = async (postId: number) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    if (!window.confirm('Delete this post?')) return;

    try {
      await deleteBatchPost(postId);
      onSuccess?.('Post deleted');
      await loadPosts();
    } catch (error) {
      console.error('Failed to delete post:', error);
      onError?.('Failed to delete post');
    }
  };

  const handleUpdatePost = async (postId: number, content: string, isPinned?: boolean, attachments?: any[]) => {
    try {
      await updateBatchPost(postId, content, isPinned ?? false);
      onSuccess?.('Post updated');
      await loadPosts();
    } catch (error) {
      console.error('Failed to update post:', error);
      onError?.('Failed to update post');
      throw error;
    }
  };

  const handleTogglePin = async (postId: number, newPinned: boolean) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    await handleUpdatePost(postId, post.content, newPinned);
  };

  const sortedPosts = React.useMemo(() => {
    return [...posts].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [posts]);

  const filteredPosts = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return sortedPosts.filter((post) => {
      const text = `${post.content || ''} ${post.authorName || ''}`.toLowerCase();
      const attachmentsText = (post.attachments || [])
        .map((att) => `${att.filename || ''} ${att.url || ''}`)
        .join(' ')
        .toLowerCase();
      const pollText = (post.poll?.question || '').toLowerCase();
      const matchesTerm = !term || text.includes(term) || attachmentsText.includes(term) || pollText.includes(term);

      if (!matchesTerm) return false;
      if (filterType === 'pinned') return post.isPinned;
      if (filterType === 'attachments') return (post.attachments?.length || 0) > 0;
      if (filterType === 'polls') return Boolean(post.poll);
      return true;
    });
  }, [sortedPosts, searchTerm, filterType]);

  const displayedPosts = React.useMemo(
    () => filteredPosts.slice(0, Math.min(visibleCount, filteredPosts.length)),
    [filteredPosts, visibleCount]
  );

  React.useEffect(() => {
    setVisibleCount(6);
  }, [searchTerm, filterType, sortedPosts.length]);

  React.useEffect(() => {
    if (!loadMoreRef.current) return;
    const target = loadMoreRef.current;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + 5, filteredPosts.length));
        }
      });
    });
    observer.observe(target);
    return () => observer.unobserve(target);
  }, [filteredPosts.length]);

  const hasNoResults = !loading && posts.length > 0 && filteredPosts.length === 0;
  const noPosts = !loading && posts.length === 0;

  if (loading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Card key={i} variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                {/* Author header */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={32} height={32} />
                  <Stack spacing={0.5}>
                    <Skeleton variant="text" width={120} />
                    <Skeleton variant="text" width={80} />
                  </Stack>
                </Stack>
                {/* Content */}
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="60%" />
                {/* Actions */}
                <Stack direction="row" spacing={2}>
                  <Skeleton variant="text" width={60} />
                  <Skeleton variant="text" width={60} />
                  <Skeleton variant="text" width={60} />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  if (expandedPost) {
    const post = posts.find((p) => p.id === expandedPost);
    if (!post) {
      return (
        <Box>
          <Typography color="danger">Post not found</Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Box mb={2}>
          <button
            onClick={() => setExpandedPost(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              cursor: 'pointer',
              padding: 0,
              fontSize: '14px',
              textDecoration: 'underline',
            }}
          >
            ← Back to feed
          </button>
        </Box>

        <BatchPostCard
          post={post}
          currentMember={currentMember}
          canEdit={!readOnly && currentMember?.id === post.authorMemberId}
          canPin={!readOnly && canPin}
          readOnly={readOnly}
          onReaction={handleReaction}
          onReplyClick={() => {}}
          onDeleteClick={handleDeletePost}
          onEditClick={handleUpdatePost}
          onPinClick={handleTogglePin}
          loading={false}
        />

        <Box mt={2}>
          <Typography level="title-lg" mb={2}>
            {post.repliesCount} {post.repliesCount === 1 ? 'Reply' : 'Replies'}
          </Typography>
          <RepliesSection
            postId={post.id}
            currentMember={currentMember}
            canReply={!readOnly}
            canDeleteAnyReply={!readOnly && canPin}
            onError={onError}
            onSuccess={onSuccess}
            onDeleteReply={handleDeleteReply}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Post Composer - Only show if user can post */}
      {canPost && currentMember && (
        <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <BatchPostComposer
            currentMember={currentMember}
            canPin={canPin}
            onSubmit={handleSubmitPost}
            loading={submitting}
            draftKey={`batch-${batchId}-post-draft`}
          />
        </Box>
      )}

      {/* Search and Filter Controls */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={{ xs: 1, sm: 1.5, md: 2 }}
        sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}
      >
        <Input
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startDecorator={<SearchIcon />}
          size="md"
          sx={{ 
            flex: 1,
            fontSize: { xs: '13px', md: '14px' },
          }}
        />
        <FormControl sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { xs: '100%', sm: '150px' } }}>
          <FormLabel sx={{ fontSize: { xs: '12px', md: '13px' }, mb: 0.5 }}>Filter</FormLabel>
          <Select
            value={filterType}
            onChange={(_, value) => value && setFilterType(value as typeof filterType)}
            size="md"
            sx={{ fontSize: { xs: '13px', md: '14px' } }}
          >
            <Option value="all">All Posts</Option>
            <Option value="pinned">📌 Pinned</Option>
            <Option value="attachments">📎 Attachments</Option>
            <Option value="polls">📊 Polls</Option>
          </Select>
        </FormControl>
      </Stack>

      {/* Empty States */}
      {noPosts ? (
        <Card 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: 'md',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <ChatIcon sx={{ fontSize: { xs: 40, md: 48 }, color: 'neutral.400', mb: 1 }} />
            <Typography color="neutral" sx={{ fontSize: { xs: '13px', md: '14px' } }}>
              {canPost ? 'No posts yet. Be the first to post!' : 'Check back later for updates.'}
            </Typography>
          </CardContent>
        </Card>
      ) : hasNoResults ? (
        <Card 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: 'md',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <SearchIcon sx={{ fontSize: { xs: 40, md: 48 }, color: 'neutral.400', mb: 1 }} />
            <Typography color="neutral" sx={{ fontSize: { xs: '13px', md: '14px' } }}>
              No posts match your search or filters.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Posts Grid */}
          <Stack 
            spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: { xs: '12px', sm: '16px', md: '20px' },
            }}
          >
            {displayedPosts.map((post) => (
              <Box
                key={post.id}
                onClick={() => setExpandedPost(post.id)}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                }}
              >
                <BatchPostCard
                  post={post}
                  currentMember={currentMember}
                  canEdit={!readOnly && currentMember?.id === post.authorMemberId}
                  canPin={!readOnly && canPin}
                  readOnly={readOnly}
                  onReaction={handleReaction}
                  onReplyClick={() => setExpandedPost(post.id)}
                  onDeleteClick={handleDeletePost}
                  onEditClick={handleUpdatePost}
                  onPinClick={handleTogglePin}
                  loading={false}
                />
              </Box>
            ))}
          </Stack>

          {/* Load More Indicator */}
          {visibleCount < filteredPosts.length && (
            <Box 
              ref={loadMoreRef} 
              sx={{ 
                py: { xs: 2, sm: 2.5, md: 3 }, 
                textAlign: 'center',
              }} 
              aria-hidden
            >
              <Typography 
                level="body-sm" 
                color="neutral"
                sx={{ fontSize: { xs: '12px', md: '13px' } }}
              >
                ↓ Scroll to load more posts…
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
